import { useState, useEffect, useRef } from "react";

const DatePickerGPT0 = ({
  label = "Sélectionner une date",
  seconds = null,
  onChange = () => {},
  mandatory = false,
  disableBeforeToday = false, // New parameter to disable dates before today
  disableAfterToday = false, // New parameter to disable dates before today
  hide_year = false, // New parameter to hide year
  minDateSeconds = null, // New parameter for custom minimum date (in seconds)
  maxDateSeconds = null, // New parameter for custom maximum date (in seconds)
  transparent = true, // New parameter for transparent background
}) => {
  const [displayDate, setDisplayDate] = useState(
    seconds ? new Date(seconds * 1000) : new Date()
  );
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const datePickerRef = useRef(null);
  const calendarRef = useRef(null);

  const daysOfWeek = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
  const months = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  // Format date as DD/MM/YYYY
  const formatDate = (seconds) => {
    let date = new Date(seconds * 1000);
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  // Update input display when selected date changes
  useEffect(() => {
    setDisplayValue(seconds ? formatDate(seconds) : "");
  }, [seconds]);

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check position on screen when opening calendar
  useEffect(() => {
    if (isOpen && datePickerRef.current) {
      const checkPosition = () => {
        const rect = datePickerRef.current.getBoundingClientRect();
        const calendarHeight = 300; // Approximate height of calendar
        const spaceBelow = window.innerHeight - rect.bottom;

        setDropUp(spaceBelow < calendarHeight);
      };

      checkPosition();
      window.addEventListener("resize", checkPosition);

      return () => {
        window.removeEventListener("resize", checkPosition);
      };
    }
  }, [isOpen]);

  // Generate calendar days for current month view
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Fix: Get day of week with Monday as 0, Sunday as 6
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert Sunday (0) to 6, and shift others back by 1
  };

  // NEW FUNCTION: Check if a date is disabled
  const isDateDisabled = (date) => {
    // Reset time part for accurate comparison
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    // Check if date is before today (if disableBeforeToday is true)
    if (disableBeforeToday) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (compareDate < today) {
        return true;
      }
    }
    if (disableAfterToday) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (compareDate > today) {
        return true;
      }
    }
    // Check against minDateSeconds if provided
    if (minDateSeconds !== null) {
      const minDateSecondsTime = new Date(minDateSeconds * 1000);
      minDateSecondsTime.setHours(0, 0, 0, 0);
      if (compareDate < minDateSecondsTime) {
        return true;
      }
    }

    // Check against maxDateSeconds if provided
    if (maxDateSeconds !== null) {
      const maxDateSecondsTime = new Date(maxDateSeconds * 1000);
      maxDateSecondsTime.setHours(0, 0, 0, 0);
      if (compareDate > maxDateSecondsTime) {
        return true;
      }
    }

    return false; // Date is enabled
  };

  const generateCalendarDays = () => {
    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const days = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        day: prevMonthDays - i,
        currentMonth: false,
        date: date,
        disabled: isDateDisabled(date), // Add disabled status
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        currentMonth: true,
        date: date,
        disabled: isDateDisabled(date), // Add disabled status
      });
    }

    // Next month days
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: i,
        currentMonth: false,
        date: date,
        disabled: isDateDisabled(date), // Add disabled status
      });
    }

    return days;
  };

  // Calendar navigation
  const prevMonth = () => {
    setDisplayDate(
      new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setDisplayDate(
      new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1)
    );
  };

  // Modified to check if date is disabled
  const handleSelectDay = (date, disabled) => {
    if (disabled) return; // Don't select disabled dates

    let seconds = date.getTime() / 1000;
    onChange(seconds);
    setIsOpen(false);
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date) => {
    if (!seconds) return false;
    let selected_date = new Date(seconds * 1000);
    return (
      date.getDate() === selected_date.getDate() &&
      date.getMonth() === selected_date.getMonth() &&
      date.getFullYear() === selected_date.getFullYear()
    );
  };

  const calendarDays = generateCalendarDays();
  let has_value = seconds !== null && seconds !== undefined && seconds !== "";

  return (
    <div
      className={`
    ${transparent ? "" : "bg-white"}
    relative w-full select-none`}
      ref={datePickerRef}
    >
      {/* Input Field */}
      <div
        tabIndex={0}
        onFocus={() => {
          setIsOpen(true);
          console.log("Focus");
        }}
        className={`px-2 w-full text-h2 rounded-xl cursor-pointer outline-none ${
          isOpen
            ? "outline-1 outline-blue-100 border-2 border-blue-300"
            : mandatory && !has_value
              ? "border-2 border-red-300"
              : "outline-none border"
        } relative flex flex-row py-1 h-11 ${has_value ? "items-end" : "items-center"}`}
      >
        {has_value && (
          <div className="absolute top-1 text-gray-300 text-h6">{label}</div>
        )}
        <input
          type="text"
          className="w-full text-h2 bg-transparent rounded-xl focus:outline-none cursor-pointer"
          placeholder={label}
          value={hide_year ? displayValue.substring(0, 5) : displayValue}
          onChange={(e) => setDisplayValue(e.target.value)}
          readOnly
        />
        <div className="absolute right-2 top-1/3 text-gray-400 cursor-pointer">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </div>

      {/* Calendar Dropdown - note the conditional positioning classes */}
      {isOpen && (
        <div
          ref={calendarRef}
          className={`absolute ${dropUp ? "bottom-full mb-1" : "top-full mt-1"} w-64 bg-white rounded shadow-lg border border-gray-200 z-10`}
        >
          {/* Header with month and navigation */}
          <div className="flex items-center justify-between p-2 border-b">
            <button
              onClick={prevMonth}
              className="p-1 rounded hover:bg-gray-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="font-medium">
              {months[displayDate.getMonth()]} {displayDate.getFullYear()}
            </div>
            <button
              onClick={nextMonth}
              className="p-1 rounded hover:bg-gray-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Days of week header */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {daysOfWeek.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {calendarDays.map((day, index) => (
              <div
                key={index}
                onClick={() =>
                  day.currentMonth && handleSelectDay(day.date, day.disabled)
                }
                className={`
                  h-6 w-6 text-center text-sm flex items-center justify-center rounded 
                  ${!day.currentMonth ? "text-gray-300" : day.disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-blue-50 cursor-pointer"}
                  ${isToday(day.date) && day.currentMonth && !day.disabled ? "border border-blue-400" : ""}
                  ${isSelected(day.date) && !day.disabled ? "bg-blue-500 text-white hover:bg-blue-600" : ""}
                  ${day.disabled && day.currentMonth ? "bg-gray-50" : ""}
                `}
              >
                {day.day}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t p-2 flex justify-end">
            <button
              onClick={() => {
                const today = new Date();
                const todaySeconds = Math.floor(today.getTime() / 1000);
                // Only set to today if today is not disabled
                if (!isDateDisabled(today)) {
                  onChange(todaySeconds);
                  setIsOpen(false);
                }
              }}
              className="text-xs text-blue-500 hover:text-blue-700"
            >
              Aujourd'hui
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePickerGPT0;
